import * as domain from '@packages/domain'
import { E, flow, O, pipe, RA, RNEA, RR, t } from '@packages/prelude'
import { Lens as _Lens, fromTraversable, Prism } from 'monocle-ts'
import { StepResolution, VisibleSteps } from '~/utils'

// -----------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------

const SelectedOptions = t.readonlyRecord(t.string, t.readonlyArray(domain.Option.Type))

const State = t.type({
  selectedOptions: SelectedOptions,
  currentStepId: t.option(domain.StepId.Type),
  allSteps: t.readonlyArray(domain.Step.Type),
  enabledSteps: t.readonlyArray(domain.StepId.Type)
})

export interface SelectedOptions extends t.TypeOf<typeof SelectedOptions> {}

export interface State extends t.TypeOf<typeof State> {}

// -----------------------------------------------------------------------------
// Lenses
// -----------------------------------------------------------------------------

export const Lens = t.getLenses(State)
const stepTraversal = fromTraversable(RA.Traversable)<domain.Step.Type>()
const optionTraversal = fromTraversable(RA.Traversable)<domain.Option.Type>()
const optionNonePrism = Prism.fromPredicate<domain.Option.Type>(domain.Option.ADT.is.OptionNone)

// Lens to get OptionNone from a specific step
const getOptionNoneByStepId = (stepId: domain.StepId.Type) =>
  Lens.allSteps
    .composeTraversal(stepTraversal)
    .filter((step: domain.Step.Type) => domain.StepId.Eq.equals(step.id, stepId))
    .composeLens(_Lens.fromProp<domain.Step.Type>()('options'))
    .composeTraversal(optionTraversal)
    .composePrism(optionNonePrism)

// -----------------------------------------------------------------------------
export const initialState: State = {
  selectedOptions: RR.empty,
  currentStepId: O.none,
  allSteps: RA.empty,
  enabledSteps: RA.empty
}

// -----------------------------------------------------------------------------
// Actions
// -----------------------------------------------------------------------------

export type Action =
  | { type: 'ACTIVE_STEP_SET'; payload: { stepId: O.Option<domain.StepId.Type> } }
  | { type: 'CONFIGURATOR_INITIALIZED'; payload: { configuratorData: domain.Configurator.Type } }
  | { type: 'ENABLED_STEPS_UPDATED'; payload: { stepIds: ReadonlyArray<domain.StepId.Type> } }
  | { type: 'NAVIGATE_NEXT' }
  | { type: 'NAVIGATE_PREVIOUS' }
  | { type: 'OPTION_NONE_SELECTED'; payload: { option: domain.Option.OptionNone } }
  | { type: 'OPTION_TOGGLED'; payload: { option: domain.Option.Option } }

export const optionToggled = (option: domain.Option.Option): Action => ({
  type: 'OPTION_TOGGLED',
  payload: { option }
})

export const optionNoneSelected = (option: domain.Option.OptionNone): Action => ({
  type: 'OPTION_NONE_SELECTED',
  payload: { option }
})

export const activeStepSet = (stepId: O.Option<domain.StepId.Type>): Action => ({
  type: 'ACTIVE_STEP_SET',
  payload: { stepId }
})

export const configuratorInitialized = (configuratorData: domain.Configurator.Type): Action => ({
  type: 'CONFIGURATOR_INITIALIZED',
  payload: { configuratorData }
})

export const navigateNext = (): Action => ({
  type: 'NAVIGATE_NEXT'
})

export const navigatePrevious = (): Action => ({
  type: 'NAVIGATE_PREVIOUS'
})

export const enabledStepsUpdated = (stepIds: ReadonlyArray<domain.StepId.Type>): Action => ({
  type: 'ENABLED_STEPS_UPDATED',
  payload: { stepIds }
})

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

const findStepById = (steps: ReadonlyArray<domain.Step.Type>, stepId: domain.StepId.Type): O.Option<domain.Step.Type> =>
  pipe(
    steps,
    RA.findFirst(step => domain.StepId.Eq.equals(step.id, stepId))
  )

const getSelectedOptionsForStep = (state: State, stepId: domain.StepId.Type): O.Option<ReadonlyArray<domain.Option.Type>> =>
  pipe(
    state.selectedOptions,
    RR.lookup(String(stepId)),
    O.alt(() => O.of(RA.empty as ReadonlyArray<domain.Option.Type>))
  )

const updateSelectedOptionsForStep = (stepId: domain.StepId.Type, options: ReadonlyArray<domain.Option.Type>) =>
  Lens.selectedOptions.modify(RR.upsertAt(String(stepId), options))

const updateEnabledStepsAfterSelection = (stepId: domain.StepId.Type, option: domain.Option.Type, originalState: State) =>
  flow(
    Lens.enabledSteps.modify(enabledStepIds => [
      ...enabledStepIds,
      ...StepResolution.resolveNextSteps(stepId, option, originalState)
    ])
  )

const setCurrentStep = (stepId: domain.StepId.Type) => Lens.currentStepId.set(O.some(stepId))

// -----------------------------------------------------------------------------
// Reducer Functions
// -----------------------------------------------------------------------------

const handleOptionToggle = (
  state: State,
  payload: {
    option: domain.Option.Type
  }
): State => {
  const { option } = payload
  return pipe(
    O.Do,
    O.apS('step', findStepById(state.allSteps, option.stepId)),
    O.bind('selectedStepOptions', () => getSelectedOptionsForStep(state, option.stepId)),
    O.bind('isSelected', ({ selectedStepOptions }) => O.some(selectedStepOptions.some(selected => selected.id === option.id))),
    O.chain(({ step, isSelected, selectedStepOptions }) =>
      pipe(
        step.selectionMode,
        domain.Step.SelectionMode.decode,
        E.map(selectionMode => {
          switch (selectionMode) {
            case 'single':
              return pipe(
                state,
                updateSelectedOptionsForStep(step.id, RA.of(option)),
                updateEnabledStepsAfterSelection(step.id, option, state),
                setCurrentStep(option.stepId)
              )
            case 'multiple':
              const updatedOptions = isSelected
                ? pipe(
                    selectedStepOptions,
                    RA.reject(selected => selected.id === option.id),
                    RNEA.fromReadonlyArray,
                    O.fold(
                      () =>
                        pipe(
                          getOptionNoneByStepId(step.id).asFold().headOption(state),
                          O.map(RA.of),
                          O.getOrElseW(() => RA.empty)
                        ),
                      RA.reject(domain.Option.ADT.is.OptionNone)
                    )
                  )
                : pipe(selectedStepOptions, RA.append(option), RA.reject(domain.Option.ADT.is.OptionNone))

              return pipe(
                state,
                updateSelectedOptionsForStep(step.id, updatedOptions),
                updateEnabledStepsAfterSelection(step.id, option, state),
                setCurrentStep(option.stepId)
              )
          }
        }),
        O.fromEither
      )
    ),
    O.getOrElse(() => state)
  )
}

const handleOptionNoneSelect = (state: State, payload: { option: domain.Option.OptionNone }): State => {
  const { option } = payload

  return pipe(
    O.Do,
    O.apS('step', findStepById(state.allSteps, option.stepId)),
    O.bind('selectedStepOptions', () => getSelectedOptionsForStep(state, option.stepId)),
    O.chain(({ step }) => {
      // If none option is selected, clear all other options
      return O.some(
        pipe(
          state,
          updateSelectedOptionsForStep(step.id, RA.of(option)),
          updateEnabledStepsAfterSelection(step.id, option, state),
          setCurrentStep(option.stepId)
        )
      )
    }),
    O.getOrElse(() => state)
  )
}

const handleNavigateNext = (state: State): State => {
  const visibleSteps = VisibleSteps.visibleSteps(state)

  return pipe(
    state,
    Lens.currentStepId.modify(
      flow(
        O.chain(currentStepId =>
          pipe(
            visibleSteps,
            RA.findIndex(step => domain.StepId.Eq.equals(step.id, currentStepId)),
            O.chain(index =>
              pipe(
                visibleSteps,
                RA.lookup(index + 1),
                O.map(step => step.id)
              )
            )
          )
        )
      )
    )
  )
}

const handleNavigatePrevious = (state: State): State => {
  const visibleSteps = VisibleSteps.visibleSteps(state)

  return pipe(
    state,
    Lens.currentStepId.modify(
      flow(
        O.chain(currentStepId =>
          pipe(
            visibleSteps,
            RA.findIndex(step => domain.StepId.Eq.equals(step.id, currentStepId)),
            O.chain(index =>
              pipe(
                visibleSteps,
                RA.lookup(index - 1),
                O.map(step => step.id)
              )
            )
          )
        )
      )
    )
  )
}

const handleConfiguratorInitialized = (state: State, payload: { configuratorData: domain.Configurator.Type }): State => {
  const { configuratorData } = payload

  // Initialize with configurator data and set first step as active
  const firstStepId = pipe(
    configuratorData.steps,
    RNEA.fromArray,
    O.map(RNEA.head),
    O.map(step => step.id)
  )

  // Initialize enabled steps with first step
  const initialEnabledSteps = pipe(
    firstStepId,
    O.fold(
      () => [],
      stepId => [stepId]
    )
  )

  return pipe(
    state,
    Lens.allSteps.set(configuratorData.steps),
    Lens.currentStepId.set(firstStepId),
    Lens.enabledSteps.set(initialEnabledSteps)
  )
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'NAVIGATE_NEXT':
      return handleNavigateNext(state)
    case 'NAVIGATE_PREVIOUS':
      return handleNavigatePrevious(state)
    case 'OPTION_TOGGLED':
      return handleOptionToggle(state, action.payload)
    case 'OPTION_NONE_SELECTED':
      return handleOptionNoneSelect(state, action.payload)
    case 'ACTIVE_STEP_SET':
      return pipe(state, Lens.currentStepId.set(action.payload.stepId))
    case 'CONFIGURATOR_INITIALIZED':
      return handleConfiguratorInitialized(state, action.payload)
    case 'ENABLED_STEPS_UPDATED':
      return pipe(state, Lens.enabledSteps.set(action.payload.stepIds))
    default:
      return state
  }
}
